'use client';

import { c as _c } from "react/compiler-runtime";
import storage from '@shared/utils/helpers/storage';
import { Monitor, Server } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import type { LaunchTarget } from '../../hooks/useJobTerminal';
import { LOCAL_RUNNER, preferredRunnerKey, RunnerProvider } from '../../hooks/useRunnerContext';
import { useRunners } from '../../hooks/useRunners';
import Modal, { ModalHeader } from '../HOC/Modal';

// Pulls in xterm and the diff viewer, which no page outside a board needs
// until a session is actually asked for.
const BareCardModal = dynamic(() => import('../pages/space/BareCardModal'), {
  ssr: false
});
type Props = {
  target: LaunchTarget;
  onClose: () => void;
};

/** A pick made in the chooser below. No runnerId = this machine (desktop). */
type Chosen = {
  runnerId?: number;
};

// Client only (the overlay mounts from a click), so this can be read during
// render instead of waiting a frame on an effect.
const onDesktop = () => Boolean(window.electronAPI?.isElectron);

/**
 * Starts a session on a board from outside it: the terminal window, opened on
 * that board with its launcher already up. The board page normally supplies
 * the RunnerProvider, so it is rebuilt here.
 *
 * The one thing the rail cannot assume is the machine. A board with a job
 * running has one, and a board the user has worked on has a stored pick;
 * anything else is asked, because work lives on a machine and choosing it is
 * the user's call.
 */
const SessionLaunchOverlay = t0 => {
  const $ = _c(48);
  const {
    target,
    onClose
  } = t0;
  const {
    runners,
    sessions,
    activities
  } = useRunners();
  let t1;
  if ($[0] !== activities || $[1] !== sessions || $[2] !== target.runnerId || $[3] !== target.spaceSlug) {
    t1 = () => target.runnerId ?? sessions.find(s => s.spaceSlug === target.spaceSlug)?.runnerId ?? activities.find(a => a.spaceSlug === target.spaceSlug)?.runnerId;
    $[0] = activities;
    $[1] = sessions;
    $[2] = target.runnerId;
    $[3] = target.spaceSlug;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [knownRunnerId] = useState(t1);
  const [stored, setStored] = useState(undefined);
  const [chosen, setChosen] = useState(null);
  let t2;
  if ($[5] !== target.spaceSlug) {
    t2 = preferredRunnerKey(target.spaceSlug);
    $[5] = target.spaceSlug;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const key = t2;
  let t3;
  let t4;
  if ($[7] !== key) {
    t3 = () => {
      let ignore = false;
      storage.getItem(key).then(name => {
        if (!ignore) {
          setStored(name);
        }
      });
      return () => {
        ignore = true;
      };
    };
    t4 = [key];
    $[7] = key;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== key) {
    t5 = async (name_0, runnerId) => {
      await storage.setItem(key, name_0);
      setChosen({
        runnerId
      });
    };
    $[10] = key;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const choose = t5;
  let t6;
  if ($[12] !== onClose || $[13] !== target.spaceSlug) {
    t6 = runnerId_0 => <RunnerProvider spaceSlug={target.spaceSlug} pinnedRunnerId={runnerId_0}><BareCardModal spaceSlug={target.spaceSlug} pinnedRunnerId={runnerId_0} startNew={true} closeHandler={onClose} /></RunnerProvider>;
    $[12] = onClose;
    $[13] = target.spaceSlug;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const openOn = t6;
  if (knownRunnerId !== undefined) {
    let t7;
    if ($[15] !== knownRunnerId || $[16] !== openOn) {
      t7 = openOn(knownRunnerId);
      $[15] = knownRunnerId;
      $[16] = openOn;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  if (chosen) {
    let t7;
    if ($[18] !== chosen.runnerId || $[19] !== openOn) {
      t7 = openOn(chosen.runnerId);
      $[18] = chosen.runnerId;
      $[19] = openOn;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  if (stored === undefined) {
    return null;
  }
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = onDesktop();
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const desktop = t7;
  const storedUsable = stored !== null && (stored === LOCAL_RUNNER && desktop || runners.some(r => r.name === stored));
  if (storedUsable) {
    setChosen({});
    return null;
  }
  if (!desktop && runners.length === 0) {
    let t8;
    if ($[22] !== openOn) {
      t8 = openOn(undefined);
      $[22] = openOn;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    return t8;
  }
  let t8;
  if ($[24] !== target.spaceSlug) {
    t8 = <h2 className="text-base font-semibold text-textPrimary">Run s/{target.spaceSlug} on</h2>;
    $[24] = target.spaceSlug;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== onClose || $[27] !== t8) {
    t9 = <ModalHeader closeHandler={onClose} headerElement={t8} />;
    $[26] = onClose;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const t10 = stored && stored !== LOCAL_RUNNER ? `${stored}, the runner this board last used, is offline.` : "This board has no runner picked yet.";
  let t11;
  if ($[29] !== t10) {
    t11 = <p className="pb-1 text-xs text-textSecondary">{t10}{" "}Its sessions stay on the machine you choose.</p>;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== choose) {
    t12 = desktop && <button type="button" className="flex w-full items-center gap-3 rounded-md border border-ds-border-standard bg-background-secondary px-3 py-2 text-left text-sm text-textPrimary transition-colors hover:bg-background-light" onClick={() => void choose(LOCAL_RUNNER)}><Monitor size={16} className="shrink-0 text-textSecondary" />This machine</button>;
    $[31] = choose;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== choose || $[34] !== runners) {
    let t14;
    if ($[36] !== choose) {
      t14 = r_0 => <button key={r_0.runnerId} type="button" className="flex w-full items-center gap-3 rounded-md border border-ds-border-standard bg-background-secondary px-3 py-2 text-left text-sm text-textPrimary transition-colors hover:bg-background-light" onClick={() => void choose(r_0.name, r_0.runnerId)}><Server size={16} className="shrink-0 text-textSecondary" /><span className="min-w-0 flex-1 truncate">{r_0.name}</span>{r_0.shared && <span className="shrink-0 text-[11px] text-textSecondary">Shared</span>}</button>;
      $[36] = choose;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    t13 = runners.map(t14);
    $[33] = choose;
    $[34] = runners;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[38] !== t11 || $[39] !== t12 || $[40] !== t13) {
    t14 = <div className="flex flex-col gap-2 p-5">{t11}{t12}{t13}</div>;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t14 || $[43] !== t9) {
    t15 = <div data-id="session-launch-runner">{t9}{t14}</div>;
    $[42] = t14;
    $[43] = t9;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== onClose || $[46] !== t15) {
    t16 = <Modal closeHandler={onClose} maxWidth={420}>{t15}</Modal>;
    $[45] = onClose;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
};
export default SessionLaunchOverlay;